/**
 * Lamp Configurator
 * 
 * Stepped drawer for products tagged 'Custom'.
 * - Intercepts add-to-cart on tagged products
 * - Loads add-on products live from Shopify (/products/HANDLE.js)
 * - Reads stock + featured_image automatically
 * - Adds lamp + selected add-ons as a bundle in one /cart/add.js call
 * - Hooks into Stretch theme via cart:refresh event
 */

(function () {
  'use strict';

  // ---------- Helpers ----------

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  function formatPrice(cents) {
    if (cents === 0) return 'Ingår';
    const sek = cents / 100;
    return sek.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' kr';
  }

  async function fetchProduct(handle) {
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.warn('[lamp-configurator] failed to load product:', handle, err);
      return null;
    }
  }

  // ---------- Configurator class ----------

  class LampConfigurator {
    constructor(element) {
      this.el = element;
      this.productId = element.dataset.productId;
      this.productHandle = element.dataset.productHandle;
      this.productTitle = element.dataset.productTitle;

      const configEl = document.getElementById('lamp-configurator-config');
      this.config = configEl ? JSON.parse(configEl.textContent) : null;
      if (!this.config) return;

      this.currentStep = 1;
      this.totalSteps = 5;
      this.selections = {
        cable: null,
        bulb: null,
        socket: null,
        engraving: false,
        engravingText: '',
        trees: 0
      };

      // Cache for loaded add-on product data (image, stock, etc.)
      this.productCache = {};

      // DOM refs
      this.overlay = $('[data-lc-overlay]', this.el);
      this.drawer = $('[data-lc-drawer]', this.el);
      this.body = $('[data-lc-body]', this.el);

      this._setup();
    }

    _setup() {
      // Open from buy buttons
      this._interceptAddToCart();

      // Close handlers
      this.overlay.addEventListener('click', () => this.close());
      $('[data-lc-close]', this.el).addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.dataset.state === 'open') this.close();
      });

      // Step navigation
      $$('[data-lc-next]', this.el).forEach(btn => btn.addEventListener('click', () => this.nextStep()));
      $$('[data-lc-prev]', this.el).forEach(btn => btn.addEventListener('click', () => this.prevStep()));

      // Engraving toggle
      const engravingToggle = $('[data-lc-engraving-toggle]', this.el);
      const engravingField = $('[data-lc-engraving-field]', this.el);
      const engravingText = $('[data-lc-engraving-text]', this.el);
      const engravingCounter = $('[data-lc-engraving-counter]', this.el);

      engravingToggle.addEventListener('change', () => {
        this.selections.engraving = engravingToggle.checked;
        engravingField.hidden = !engravingToggle.checked;
        if (!engravingToggle.checked) {
          engravingText.value = '';
          engravingCounter.textContent = '0';
          this.selections.engravingText = '';
        } else {
          setTimeout(() => engravingText.focus(), 50);
        }
      });

      engravingText.addEventListener('input', () => {
        engravingCounter.textContent = engravingText.value.length;
        this.selections.engravingText = engravingText.value.trim();
      });

      // Tree quantity
      const treeCount = $('[data-lc-tree-count]', this.el);
      $('[data-lc-tree-decrement]', this.el).addEventListener('click', () => {
        if (this.selections.trees > 0) {
          this.selections.trees--;
          treeCount.textContent = this.selections.trees;
        }
      });
      $('[data-lc-tree-increment]', this.el).addEventListener('click', () => {
        if (this.selections.trees < 99) {
          this.selections.trees++;
          treeCount.textContent = this.selections.trees;
        }
      });

      // Submit
      $('[data-lc-submit]', this.el).addEventListener('click', () => this.submit());
    }

    _interceptAddToCart() {
      // Find the Stretch product form on the page
      const productForm = document.querySelector('product-form form');
      if (!productForm) return;

      productForm.addEventListener('submit', (e) => {
        // Only intercept if the lamp variant is available + selected
        const variantInput = productForm.querySelector('input[name="id"]');
        if (!variantInput || !variantInput.value) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        this.selectedLampVariantId = variantInput.value;

        // Read quantity (Stretch may have a quantity input)
        const qtyInput = productForm.querySelector('input[name="quantity"]');
        this.selectedLampQuantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        this.open();
      }, true); // capture phase to run before Stretch's own handlers
    }

    async open() {
      document.body.classList.add('lamp-configurator-open');
      this.overlay.hidden = false;
      this.drawer.hidden = false;

      // Trigger CSS transition
      requestAnimationFrame(() => {
        this.overlay.dataset.state = 'open';
        this.drawer.dataset.state = 'open';
      });

      // Reset to step 1
      this.goToStep(1);

      // Load all add-on products
      await this._loadAllProducts();

      // Render step 1 options
      this._renderOptions('cables');
      this._renderOptions('bulbs');
      this._renderOptions('sockets');
    }

    close() {
      this.overlay.dataset.state = '';
      this.drawer.dataset.state = '';
      document.body.classList.remove('lamp-configurator-open');

      setTimeout(() => {
        this.overlay.hidden = true;
        this.drawer.hidden = true;
      }, 400);
    }

    async _loadAllProducts() {
      const allHandles = [
        ...this.config.cables.filter(c => c.handle).map(c => c.handle),
        ...this.config.bulbs.filter(b => b.handle).map(b => b.handle),
        ...this.config.sockets.filter(s => s.handle).map(s => s.handle)
      ];

      // Also load engraving product to display its featured image
      if (this.config.engravingHandle) allHandles.push(this.config.engravingHandle);

      const uncached = allHandles.filter(h => !this.productCache[h]);
      if (uncached.length === 0) return;

      const results = await Promise.all(uncached.map(h => fetchProduct(h)));
      uncached.forEach((handle, i) => {
        if (results[i]) this.productCache[handle] = results[i];
      });

      // After loading, render extras step images (in case it's already rendered)
      this._renderExtrasImages();
    }

    _renderExtrasImages() {
      // Render large featured image for engraving
      const engravingThumbEl = $('[data-lc-engraving-thumb]', this.el);
      if (engravingThumbEl && this.config.engravingHandle && this.productCache[this.config.engravingHandle]) {
        const product = this.productCache[this.config.engravingHandle];
        if (product.featured_image) {
          engravingThumbEl.innerHTML = `<img src="${product.featured_image}&width=400" alt="" />`;
          engravingThumbEl.classList.add('has-image');
        }
      }
    }

    _renderOptions(type) {
      const container = $(`[data-lc-options="${type}"]`, this.el);
      if (!container) return;

      const items = this.config[type] || [];
      container.innerHTML = '';

      items.forEach((item, idx) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lamp-configurator__option';
        button.dataset.lcOption = type;
        button.dataset.lcIndex = idx;

        // Determine availability
        let available = true;
        let imageUrl = null;
        if (item.handle && this.productCache[item.handle]) {
          const product = this.productCache[item.handle];
          available = product.available !== false;
          imageUrl = product.featured_image;
        }

        if (!available) {
          button.classList.add('is-unavailable');
          button.disabled = true;
        }

        // Image
        let imageHtml;
        if (item.eco) {
          imageHtml = `
            <div class="lamp-configurator__option-image lamp-configurator__option-image--placeholder">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z" style="color:#2d5a3d"/>
              </svg>
            </div>`;
        } else if (imageUrl) {
          imageHtml = `<img class="lamp-configurator__option-image" src="${imageUrl}&width=120" alt="${item.label}" loading="lazy" />`;
        } else {
          imageHtml = `
            <div class="lamp-configurator__option-image lamp-configurator__option-image--placeholder">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>
              </svg>
            </div>`;
        }

        // Meta line (eco / stock)
        let metaHtml = '';
        if (item.eco) {
          metaHtml = `
            <span class="lamp-configurator__option-eco">
              Miljövänligt val
            </span>`;
        } else if (!available) {
          metaHtml = `
            <span class="lamp-configurator__option-stock lamp-configurator__option-stock--out">
              <span class="lamp-configurator__option-stock-dot"></span>
              Slut i lager
            </span>`;
        } else {
          metaHtml = `<span class="lamp-configurator__option-price">Ingår</span>`;
        }

        button.innerHTML = `
          ${imageHtml}
          <div class="lamp-configurator__option-text">
            <span class="lamp-configurator__option-label">${item.label}</span>
            ${item.info ? `<span class="lamp-configurator__option-info">${item.info}</span>` : ''}
            <div class="lamp-configurator__option-meta">${metaHtml}</div>
          </div>
          <span class="lamp-configurator__option-check">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </span>
        `;

        if (available) {
          button.addEventListener('click', () => this._selectOption(type, idx));
        }

        container.appendChild(button);
      });
    }

    _selectOption(type, idx) {
      const stepMap = { cables: 'cable', bulbs: 'bulb', sockets: 'socket' };
      const key = stepMap[type];
      this.selections[key] = idx;

      // Update visual selection
      $$(`[data-lc-options="${type}"] .lamp-configurator__option`, this.el).forEach((btn, i) => {
        btn.classList.toggle('is-selected', i === idx);
      });

      // Enable next button on this step
      const stepNum = { cables: 1, bulbs: 2, sockets: 3 }[type];
      const stepEl = $(`[data-lc-step="${stepNum}"]`, this.el);
      const nextBtn = $('[data-lc-next]', stepEl);
      if (nextBtn) nextBtn.disabled = false;
    }

    goToStep(step) {
      this.currentStep = step;

      // Show only the current step
      $$('[data-lc-step]', this.el).forEach(el => {
        el.classList.toggle('is-active', parseInt(el.dataset.lcStep, 10) === step);
      });

      // Update progress indicator
      $$('.lamp-configurator__progress-step', this.el).forEach(el => {
        const s = parseInt(el.dataset.step, 10);
        el.classList.remove('is-active', 'is-complete');
        if (s < step) el.classList.add('is-complete');
        else if (s === step) el.classList.add('is-active');
      });

      // Scroll back to top of body
      this.body.scrollTop = 0;

      // If summary step, render it
      if (step === 5) this._renderSummary();
    }

    nextStep() {
      if (this.currentStep < this.totalSteps) this.goToStep(this.currentStep + 1);
    }

    prevStep() {
      if (this.currentStep > 1) this.goToStep(this.currentStep - 1);
    }

    _renderSummary() {
      const container = $('[data-lc-summary]', this.el);
      const rows = [];
      let total = 0; // we don't actually know lamp price here — Shopify cart will sum it. We only show extras.

      // Lamp row
      rows.push(`
        <div class="lamp-configurator__summary-row lamp-configurator__summary-row--lamp">
          <div class="lamp-configurator__summary-row-content">
            <p class="lamp-configurator__summary-row-label">Lampa</p>
            <p class="lamp-configurator__summary-row-value">${this.productTitle}</p>
          </div>
        </div>
      `);

      // Cable
      if (this.selections.cable !== null) {
        const cable = this.config.cables[this.selections.cable];
        rows.push(this._summaryRowFor('Sladd', cable, 0));
      }

      // Bulb
      if (this.selections.bulb !== null) {
        const bulb = this.config.bulbs[this.selections.bulb];
        rows.push(this._summaryRowFor('Ljuskälla', bulb, 0));
      }

      // Socket
      if (this.selections.socket !== null) {
        const socket = this.config.sockets[this.selections.socket];
        rows.push(this._summaryRowFor('Takkontakt', socket, 0));
      }

      // Engraving
      if (this.selections.engraving && this.selections.engravingText) {
        const price = this.config.engravingPrice;
        total += price;
        let engravingImg = '';
        if (this.config.engravingHandle && this.productCache[this.config.engravingHandle] && this.productCache[this.config.engravingHandle].featured_image) {
          engravingImg = `<img class="lamp-configurator__summary-row-image" src="${this.productCache[this.config.engravingHandle].featured_image}&width=120" alt="" />`;
        }
        rows.push(`
          <div class="lamp-configurator__summary-row">
            ${engravingImg}
            <div class="lamp-configurator__summary-row-content">
              <p class="lamp-configurator__summary-row-label">Personlig skylt</p>
              <p class="lamp-configurator__summary-row-value">Graverad träskylt</p>
              <p class="lamp-configurator__summary-row-extra">"${this._escape(this.selections.engravingText)}"</p>
            </div>
            <span class="lamp-configurator__summary-row-price">${formatPrice(price)}</span>
          </div>
        `);
      }

      // Trees
      if (this.selections.trees > 0) {
        const price = this.config.treePrice * this.selections.trees;
        total += price;
        rows.push(`
          <div class="lamp-configurator__summary-row">
            <div class="lamp-configurator__summary-row-content">
              <p class="lamp-configurator__summary-row-label">Miljöbidrag</p>
              <p class="lamp-configurator__summary-row-value">Plantera ${this.selections.trees} ${this.selections.trees === 1 ? 'träd' : 'träd'}</p>
            </div>
            <span class="lamp-configurator__summary-row-price">${formatPrice(price)}</span>
          </div>
        `);
      }

      // Total of extras — always shown
      const totalRow = `
        <div class="lamp-configurator__summary-total">
          <span class="lamp-configurator__summary-total-label">Summa tillval</span>
          <span class="lamp-configurator__summary-total-amount">${total === 0 ? 'Ingår' : formatPrice(total)}</span>
        </div>
      `;

      container.innerHTML = rows.join('') + totalRow;
    }

    _summaryRowFor(category, item, price) {
      let imageHtml = '';
      if (item.handle && this.productCache[item.handle] && this.productCache[item.handle].featured_image) {
        imageHtml = `<img class="lamp-configurator__summary-row-image" src="${this.productCache[item.handle].featured_image}&width=120" alt="" />`;
      }
      const priceHtml = item.eco
        ? `<span class="lamp-configurator__summary-row-price lamp-configurator__summary-row-price--free">Ingår</span>`
        : (price === 0
            ? `<span class="lamp-configurator__summary-row-price lamp-configurator__summary-row-price--free">Ingår</span>`
            : `<span class="lamp-configurator__summary-row-price">${formatPrice(price)}</span>`);

      return `
        <div class="lamp-configurator__summary-row">
          ${imageHtml}
          <div class="lamp-configurator__summary-row-content">
            <p class="lamp-configurator__summary-row-label">${category}</p>
            <p class="lamp-configurator__summary-row-value">${item.label}</p>
          </div>
          ${priceHtml}
        </div>
      `;
    }

    _escape(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    async submit() {
      const submitBtn = $('[data-lc-submit]', this.el);
      const submitLabel = $('[data-lc-submit-label]', this.el);
      const submitSpinner = $('[data-lc-submit-spinner]', this.el);

      submitBtn.disabled = true;
      submitLabel.textContent = 'Lägger till…';
      submitSpinner.hidden = false;

      // Build bundle ID — used to group items in cart visually
      const bundleId = 'lamp-' + Date.now() + '-' + Math.floor(Math.random() * 10000);

      const items = [];

      // 1) The lamp itself
      items.push({
        id: parseInt(this.selectedLampVariantId, 10),
        quantity: this.selectedLampQuantity || 1,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_role': 'main'
        }
      });

      // Helper for add-on item
      const addAddon = (variantId, label, extraProps) => {
        const properties = {
          '_bundle_id': bundleId,
          '_bundle_role': 'addon',
          '_addon_label': label
        };
        if (extraProps) Object.assign(properties, extraProps);
        items.push({
          id: parseInt(variantId, 10),
          quantity: 1,
          properties
        });
      };

      // 2) Cable
      if (this.selections.cable !== null) {
        const cable = this.config.cables[this.selections.cable];
        if (cable.id) addAddon(cable.id, 'Sladd: ' + cable.label);
      }

      // 3) Bulb
      if (this.selections.bulb !== null) {
        const bulb = this.config.bulbs[this.selections.bulb];
        if (bulb.id) addAddon(bulb.id, 'Ljuskälla: ' + bulb.label);
      }

      // 4) Socket
      if (this.selections.socket !== null) {
        const socket = this.config.sockets[this.selections.socket];
        if (socket.id) addAddon(socket.id, 'Takkontakt: ' + socket.label);
      }

      // 5) Engraving
      if (this.selections.engraving && this.selections.engravingText) {
        addAddon(this.config.engravingId, 'Personlig graverad skylt', {
          'Gravyrtext': this.selections.engravingText
        });
      }

      // 6) Trees
      if (this.selections.trees > 0) {
        items.push({
          id: parseInt(this.config.treeId, 10),
          quantity: this.selections.trees,
          properties: {
            '_bundle_id': bundleId,
            '_bundle_role': 'addon',
            '_addon_label': 'Plantera träd'
          }
        });
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error((err && err.description) || 'Något gick fel.');
        }

        // Refresh Stretch cart UI
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        // Close drawer
        this.close();

        // Open Stretch's own cart drawer if it exists
        setTimeout(() => {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.show === 'function') {
            cartDrawer.show();
          } else {
            // Fallback: trigger a click on a cart link if drawer not present
            const cartLink = document.querySelector('a[href="/cart"], a[href*="/cart"]');
            // Don't navigate — just hope cart:refresh triggered drawer
          }
        }, 450);

        // Reset selections for next use
        this._reset();

      } catch (err) {
        console.error('[lamp-configurator] submit failed:', err);
        alert('Något gick fel: ' + err.message + '\n\nPröva igen eller kontakta oss om problemet kvarstår.');
        submitBtn.disabled = false;
        submitLabel.textContent = 'Lägg till i varukorg';
        submitSpinner.hidden = true;
      }
    }

    _reset() {
      this.selections = {
        cable: null,
        bulb: null,
        socket: null,
        engraving: false,
        engravingText: '',
        trees: 0
      };

      // Reset UI elements
      const eToggle = $('[data-lc-engraving-toggle]', this.el);
      const eText = $('[data-lc-engraving-text]', this.el);
      const eCounter = $('[data-lc-engraving-counter]', this.el);
      const eField = $('[data-lc-engraving-field]', this.el);
      const treeCount = $('[data-lc-tree-count]', this.el);

      if (eToggle) eToggle.checked = false;
      if (eField) eField.hidden = true;
      if (eText) eText.value = '';
      if (eCounter) eCounter.textContent = '0';
      if (treeCount) treeCount.textContent = '0';

      $$('.lamp-configurator__option', this.el).forEach(o => o.classList.remove('is-selected'));
      $$('[data-lc-next]', this.el).forEach(b => {
        // Re-disable next buttons on steps that need a selection
        const step = b.closest('[data-lc-step]');
        if (step && ['1', '2', '3'].includes(step.dataset.lcStep)) {
          b.disabled = true;
        }
      });

      // Reset submit button
      const submitBtn = $('[data-lc-submit]', this.el);
      const submitLabel = $('[data-lc-submit-label]', this.el);
      const submitSpinner = $('[data-lc-submit-spinner]', this.el);
      if (submitBtn) submitBtn.disabled = false;
      if (submitLabel) submitLabel.textContent = 'Lägg till i varukorg';
      if (submitSpinner) submitSpinner.hidden = true;
    }
  }

  // Custom element wrapper
  if (!customElements.get('lamp-configurator')) {
    class LampConfiguratorElement extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;
        new LampConfigurator(this);
      }
    }
    customElements.define('lamp-configurator', LampConfiguratorElement);
  }

})();
